<script setup lang="ts">

import { ref, computed, onBeforeMount, onBeforeUnmount } from "vue"
import { useGetDocs, postData, updateMeta, deleteData } from "../../mixins/modularFirebaseOperations"
import { modalClosed } from "../../mixins/visualFormatters"
import AcMediumModal from "../reusable/teleports/acMediumModal.vue"
import InventoryMetaForm from "../reusable/form-widgets/inventoryMetaForm.vue"
import resetBackground from "@/mixins/resetBackground"
import removeBackground from "@/mixins/removeBackground"
import type { AcInventoryWorksite } from "../../types/inventory"

const COLLECTION = "inventory/worksites/list"

const isAddingMeta = ref<boolean>(false)
const editedMeta = ref<AcInventoryWorksite | undefined>(undefined)
const showInactive = ref<boolean>(false)
const { docs, isLoadingDocs } = useGetDocs<AcInventoryWorksite>(COLLECTION)

// By default only active worksites are listed; inactive ones are revealed on demand.
const visibleMetas = computed(() =>
    showInactive.value ? docs.value : docs.value.filter((meta) => meta.isActive)
)

const handleAddMeta = async function(meta: AcInventoryWorksite) {
    const newMeta = { ...meta, isActive: true }
    const docRef = await postData(newMeta, COLLECTION)
    docs.value.push({ ...newMeta, id: docRef as string })
    isAddingMeta.value = false
    modalClosed()
}

const handleDelete = async function(metaId: string) {
    if (!confirm("Bu şantiyeyi silmek istediğinize emin misiniz?")) return
    await deleteData(COLLECTION, metaId)
    docs.value = docs.value.filter((meta) => meta.id !== metaId)
}

const handleUpdatedData = async function(meta: AcInventoryWorksite) {
    await updateMeta(COLLECTION, meta.id!, meta)
    const updatedMeta = docs.value.find((existingMeta) => existingMeta.id === meta.id)
    if (updatedMeta) {
        updatedMeta.name = meta.name
        updatedMeta.location = meta.location
        updatedMeta.color = meta.color
    }
    editedMeta.value = undefined
    modalClosed()
}

const toggleActive = async function(meta: AcInventoryWorksite & { id: string }) {
    const newValue = !meta.isActive
    await updateMeta(COLLECTION, meta.id, { isActive: newValue })
    meta.isActive = newValue
}

onBeforeMount(() => {
  removeBackground.methods.removeBackground()
})

onBeforeUnmount(() => {
  resetBackground.methods.resetBackground()
})

</script>

<template>
    <div class="main-container mt-8 content-start items-start">
        <div class="flex col-start-1 col-end-13 justify-between items-center-w-full">
            <h2 class="ac-large-title">Şantiyeler</h2>

            <div class="action-row col-start-1 col-end-13 flex items-center justify-end gap-6">
                <label class="flex items-center gap-2 text-xl cursor-pointer">
                    <input type="checkbox" v-model="showInactive">
                    Pasif şantiyeleri göster
                </label>
                <button type="button" @click="isAddingMeta = true" class="ac-add-button">
                    <img class="mr-4 filter-white" width="20" src="../../assets/icons/add.png" alt="">
                    Yeni Şantiye Ekle
                </button>
            </div>
        </div>
        <table v-if="!isLoadingDocs" class="alt-table col-start-1 col-end-13 w-full mt-5">
            <thead>
                <tr>
                    <th>Renk</th>
                    <th>Şantiye</th>
                    <th>Lokasyon</th>
                    <th>Durum</th>
                    <th>İşlemler</th>
                </tr>
            </thead>
            <tr class="hover:bg-gray-200 relative cursor-pointer" v-for="meta in visibleMetas" :key="meta.id">
                <td class="text-overflow w-24">
                    <div class="w-6 h-6 rounded-full" :style="{ backgroundColor: meta.color }"></div>
                </td>
                <td class="text-overflow"> {{ meta.name }}</td>
                <td class="text-overflow"> {{ meta.location || "-" }}</td>
                <td class="text-overflow">
                    <span :class="meta.isActive ? 'status-pill status-pill--active' : 'status-pill status-pill--inactive'">
                        {{ meta.isActive ? "Aktif" : "Pasif" }}
                    </span>
                </td>
                <td class="flex">
                    <button type="button" class="table-button" @click="toggleActive(meta)">
                        {{ meta.isActive ? "Pasife Al" : "Aktife Al" }}
                    </button>
                    <button type="button" class="table-button" @click="editedMeta = meta">
                        Düzenle
                    </button>
                    <button type="button" class="table-button" @click="handleDelete(meta.id!)">
                        Sil
                    </button>
                </td>
            </tr>
        </table>

    <teleport to="body">
        <transition
            appear
            type="animation"
            enter-active-class="animate__animated animate__fadeIn animate__fast"
            leave-active-class="animate__animated animate__fadeOut animate__faster"
        >
            <ac-medium-modal class="lg:!top-44 overflow-y-scroll" @modal-closed="isAddingMeta = false; editedMeta = undefined" v-if="isAddingMeta || editedMeta" height="fit-content" width="40vw" title="Şantiye">
                <inventory-meta-form
                metaTitle="Şantiye"
                :hasColor="true"
                :hasLocation="true"
                :existingMeta="editedMeta"
                @meta-added="handleAddMeta($event)"
                @meta-updated="handleUpdatedData($event)"
                />
            </ac-medium-modal>
       </transition>
  </teleport>
    </div>
</template>

<style lang="scss">

@import "../../styles/global/helpers.scss";
@import "../../styles/global/buttons.scss";
@import "../../styles/global/tables.scss";

</style>

<style scoped>
.status-pill {
    display: inline-block;
    padding: 0.2rem 0.9rem;
    border-radius: 9999px;
    font-size: 1.2rem;
    font-weight: 600;
}
.status-pill--active {
    background-color: #dcfce7;
    color: #16a34a;
}
.status-pill--inactive {
    background-color: #f3f4f6;
    color: #6b7280;
}
</style>
